import React, { useCallback } from 'react';
import styled from 'styled-components';
import {
  Content,
  Divider,
  SegmentedControl,
  SegmentedControlItem,
  Stack,
  Text,
  Secondary,
} from '@kinesis/bungle';
import {
  domains,
  authScopes,
  domainWildcard,
  domainReadWildcard,
  resourceRead,
  resourceWrite,
  scopesSatisfy,
} from '@/scopes';
import type { Domain, Resource } from '@/scopes';
import { noop } from 'lodash/fp';

const Row = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
});

const RowInfo = styled.div({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

const RowControl = styled.div({
  flexShrink: 0,
});

const ResourceRow = styled(Row)({
  paddingLeft: 12,
});

// Determine the domain-level mode from the current scopes.
const deriveDomainMode = (
  scopes: string[],
  domain: Domain,
): 'everything' | 'read' | 'custom' => {
  if (scopesSatisfy(scopes, domainWildcard(domain))) return 'everything';
  if (scopesSatisfy(scopes, domainReadWildcard(domain))) return 'read';
  return 'custom';
};

// Determine the auth domain mode from the current scopes.
const deriveAuthMode = (scopes: string[]): 'everything' | 'custom' => {
  if (scopesSatisfy(scopes, 'auth:*')) return 'everything';
  return 'custom';
};

// Determine the resource-level access from the current scopes.
const deriveResourceAccess = (
  scopes: string[],
  domain: Domain,
  resource: Resource,
): 'none' | 'read' | 'write' => {
  if (scopesSatisfy(scopes, resourceWrite(domain, resource))) return 'write';
  if (scopesSatisfy(scopes, resourceRead(domain, resource))) return 'read';
  return 'none';
};

// Remove all scopes belonging to a domain.
const removeDomainScopes = (scopes: string[], domain: Domain): string[] =>
  scopes.filter(
    (s) => !s.startsWith(`${domain.key}:`) && s !== `${domain.key}:*`,
  );

// Remove all auth scopes.
const removeAuthScopes = (scopes: string[]): string[] =>
  scopes.filter((s) => !s.startsWith('auth:'));

// Expand a domain to individual resource write scopes (everything equivalent).
const expandDomainToResourceScopes = (domain: Domain): string[] =>
  domain.resources.map((r) => resourceWrite(domain, r));

// Expand a domain to individual resource read scopes.
const expandDomainToReadScopes = (domain: Domain): string[] =>
  domain.resources.map((r) => resourceRead(domain, r));

const DomainSection = ({
  domain,
  scopes,
  onChange,
}: {
  domain: Domain;
  scopes: string[];
  onChange: (scopes: string[]) => void;
}) => {
  const mode = deriveDomainMode(scopes, domain);

  const onModeChange = useCallback(
    (next: string | number) => {
      const rest = removeDomainScopes(scopes, domain);
      switch (next) {
        case 'everything':
          onChange([...rest, domainWildcard(domain)]);
          break;
        case 'read':
          onChange([...rest, domainReadWildcard(domain)]);
          break;
        case 'custom':
          if (mode === 'everything') {
            onChange([...rest, ...expandDomainToResourceScopes(domain)]);
          } else if (mode === 'read') {
            onChange([...rest, ...expandDomainToReadScopes(domain)]);
          } else {
            onChange(scopes);
          }
          break;
      }
    },
    [scopes, domain, mode, onChange],
  );

  const onResourceChange = useCallback(
    (resource: Resource, access: string | number) => {
      const rest = scopes.filter(
        (s) =>
          s !== resourceRead(domain, resource) &&
          s !== resourceWrite(domain, resource),
      );
      switch (access) {
        case 'read':
          onChange([...rest, resourceRead(domain, resource)]);
          break;
        case 'write':
          onChange([...rest, resourceWrite(domain, resource)]);
          break;
        default:
          onChange(rest);
          break;
      }
    },
    [scopes, domain, onChange],
  );

  return (
    <Stack space='small'>
      <Row>
        <RowInfo>
          <Text>{domain.label}</Text>
          <Text>
            <Secondary>{domain.description}</Secondary>
          </Text>
        </RowInfo>
        <RowControl>
          <SegmentedControl
            magnitude='medium'
            value={mode}
            onChange={onModeChange}
          >
            <SegmentedControlItem value='everything'>
              Everything
            </SegmentedControlItem>
            <SegmentedControlItem value='read'>Read</SegmentedControlItem>
            <SegmentedControlItem value='custom'>Custom</SegmentedControlItem>
          </SegmentedControl>
        </RowControl>
      </Row>
      {mode === 'custom' &&
        domain.resources.map((resource) => {
          const access = deriveResourceAccess(scopes, domain, resource);
          return (
            <ResourceRow key={resource.key}>
              <RowInfo>
                <Text>{resource.label}</Text>
                <Text>
                  <Secondary>{resource.description}</Secondary>
                </Text>
              </RowInfo>
              <RowControl>
                <SegmentedControl
                  magnitude='medium'
                  value={access}
                  onChange={(v: string | number) =>
                    onResourceChange(resource, v)
                  }
                >
                  <SegmentedControlItem value='none'>None</SegmentedControlItem>
                  <SegmentedControlItem value='read'>Read</SegmentedControlItem>
                  <SegmentedControlItem value='write'>
                    Write
                  </SegmentedControlItem>
                </SegmentedControl>
              </RowControl>
            </ResourceRow>
          );
        })}
    </Stack>
  );
};

const AuthSection = ({
  scopes,
  onChange,
}: {
  scopes: string[];
  onChange: (scopes: string[]) => void;
}) => {
  const mode = deriveAuthMode(scopes);

  const onModeChange = useCallback(
    (next: string | number) => {
      const rest = removeAuthScopes(scopes);
      switch (next) {
        case 'everything':
          onChange([...rest, 'auth:*']);
          break;
        case 'custom':
          if (mode === 'everything') {
            onChange([...rest, ...authScopes.map((s) => s.scope)]);
          } else {
            onChange(scopes);
          }
          break;
      }
    },
    [scopes, mode, onChange],
  );

  const onToggle = useCallback(
    (scope: string, on: string | number) => {
      if (on === 'on') {
        onChange([...scopes, scope]);
      } else {
        onChange(scopes.filter((s) => s !== scope));
      }
    },
    [scopes, onChange],
  );

  return (
    <Stack space='small'>
      <Row>
        <RowInfo>
          <Text>Auth</Text>
          <Text>
            <Secondary>Authentication and identity operations</Secondary>
          </Text>
        </RowInfo>
        <RowControl>
          <SegmentedControl
            magnitude='medium'
            value={mode}
            onChange={onModeChange}
          >
            <SegmentedControlItem value='everything'>
              Everything
            </SegmentedControlItem>
            <SegmentedControlItem value='custom'>Custom</SegmentedControlItem>
          </SegmentedControl>
        </RowControl>
      </Row>
      {mode === 'custom' &&
        authScopes.map((authScope) => {
          const granted = scopes.includes(authScope.scope);
          return (
            <ResourceRow key={authScope.scope}>
              <RowInfo>
                <Text>{authScope.label}</Text>
                <Text>
                  <Secondary>{authScope.description}</Secondary>
                </Text>
              </RowInfo>
              <RowControl>
                <SegmentedControl
                  magnitude='medium'
                  value={granted ? 'on' : 'off'}
                  onChange={(v: string | number) =>
                    onToggle(authScope.scope, v)
                  }
                >
                  <SegmentedControlItem value='off'>
                    Disable
                  </SegmentedControlItem>
                  <SegmentedControlItem value='on'>Enable</SegmentedControlItem>
                </SegmentedControl>
              </RowControl>
            </ResourceRow>
          );
        })}
    </Stack>
  );
};

const DEFAULT_DESCRIPTION =
  'Scopes limit what this API key can access. They do not grant permissions ' +
  'beyond those of the owning user or service account.';
const DEFAULT_CAPTION = 'Choose which API operations this key can perform';

const ScopePicker = (props: ScopePickerProps) => {
  const {
    value = ['*'],
    onChange = noop,
    description = DEFAULT_DESCRIPTION,
    caption = DEFAULT_CAPTION,
  } = props;

  const isEverything = value.includes('*');

  const onTopLevelChange = useCallback(
    (next: string | number) => {
      if (next === 'everything') {
        onChange(['*']);
      } else {
        onChange([...domains.map((d) => domainWildcard(d)), 'auth:*']);
      }
    },
    [onChange],
  );

  return (
    <Stack space='medium'>
      <Content background='gray' padding='small' borderRadiusSize='small'>
        <Text>{description}</Text>
      </Content>
      <Row>
        <RowInfo>
          <Text>Scope</Text>
          <Text>
            <Secondary>{caption}</Secondary>
          </Text>
        </RowInfo>
        <RowControl>
          <SegmentedControl
            magnitude='medium'
            value={isEverything ? 'everything' : 'custom'}
            onChange={onTopLevelChange}
          >
            <SegmentedControlItem value='everything'>
              Everything
            </SegmentedControlItem>
            <SegmentedControlItem value='custom'>Custom</SegmentedControlItem>
          </SegmentedControl>
        </RowControl>
      </Row>
      {!isEverything && (
        <>
          {domains.map((domain) => (
            <React.Fragment key={domain.key}>
              <Divider />
              <DomainSection
                domain={domain}
                scopes={value}
                onChange={onChange}
              />
            </React.Fragment>
          ))}
          <Divider />
          <AuthSection scopes={value} onChange={onChange} />
        </>
      )}
    </Stack>
  );
};

type ScopePickerProps = {
  value?: string[];
  onChange?: (scopes: string[]) => void;
  description?: string;
  caption?: string;
};

export type { ScopePickerProps };
export { ScopePicker };
